/**
 * SoleadLab 2.0 — Asset Tree Component
 *
 * B3.5: Read-only tree view of the asset_nodes topology for a site.
 *
 * Renders the asset graph as a nested list: roots at the top level,
 * children indented by depth. Each node shows:
 *   - asset_type badge
 *   - display_name (falling back to canonical_label)
 *   - canonical_label (as a secondary identifier)
 *
 * The tree is driven entirely by asset_nodes from the backend — it does
 * not reimplement topology logic. If no nodes exist, a hint guides the
 * operator to save base data (which triggers the sync).
 *
 * Usage:
 *   <AssetTree siteId="site-123" />
 */

import { useEffect } from 'react';
import { useAssetNodeStore } from '@/stores/assetNodeStore';
import type { AssetNode } from '@/services/assetNodeApi';

interface AssetTreeProps {
  siteId: string;
}

// ─── Type sort order ─────────────────────────────────────────────────────────

const TYPE_ORDER: Record<string, number> = {
  site: 0,
  station: 1,
  inverter: 2,
  mppt: 3,
  dccb: 4,
  string: 5,
  module: 6,
};

function typeSort(a: AssetNode, b: AssetNode): number {
  const ao = TYPE_ORDER[a.assetType] ?? 99;
  const bo = TYPE_ORDER[b.assetType] ?? 99;
  if (ao !== bo) return ao - bo;
  return a.canonicalLabel.localeCompare(b.canonicalLabel);
}

// ─── Single Node Row (recursive) ─────────────────────────────────────────────

interface NodeRowProps {
  node: AssetNode;
  getChildren: (parentId: string | null) => AssetNode[];
  depth: number;
}

function AssetNodeRow({ node, getChildren, depth }: NodeRowProps) {
  const children = getChildren(node.id).sort(typeSort);

  return (
    <li
      className="asset-tree__node"
      style={{ '--asset-depth': depth } as React.CSSProperties}
    >
      <div className="asset-tree__row">
        <span className={`asset-tree__type-badge asset-tree__type-badge--${node.assetType}`}>
          {node.assetType}
        </span>
        <span className="asset-tree__label">
          {node.displayName ?? node.canonicalLabel}
        </span>
        {node.displayName && (
          <span className="asset-tree__canonical" title="Canonical label">
            {node.canonicalLabel}
          </span>
        )}
      </div>
      {children.length > 0 && (
        <ul className="asset-tree__children">
          {children.map((child) => (
            <AssetNodeRow
              key={child.id}
              node={child}
              getChildren={getChildren}
              depth={depth + 1}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

// ─── Tree Component ──────────────────────────────────────────────────────────

export default function AssetTree({ siteId }: AssetTreeProps) {
  const items = useAssetNodeStore((s) => s.items);
  const loading = useAssetNodeStore((s) => s.loading);
  const error = useAssetNodeStore((s) => s.error);
  const setSiteId = useAssetNodeStore((s) => s.setSiteId);
  const fetchItems = useAssetNodeStore((s) => s.fetchItems);
  const getChildren = useAssetNodeStore((s) => s.getChildren);

  useEffect(() => {
    setSiteId(siteId);
  }, [siteId, setSiteId]);

  useEffect(() => {
    if (siteId) {
      fetchItems();
    }
  }, [siteId, fetchItems]);

  if (loading) {
    return <div className="asset-tree__status">Loading asset tree…</div>;
  }

  if (error) {
    return <div className="asset-tree__status asset-tree__error">{error}</div>;
  }

  if (items.length === 0) {
    return (
      <div className="asset-tree__status">
        No asset nodes found.
        <span className="asset-tree__hint">
          {' '}Save base data to generate the asset topology tree.
        </span>
      </div>
    );
  }

  // Roots: nodes whose parent is absent from the current item set
  const itemIds = new Set(items.map((n) => n.id));
  const roots = items
    .filter((n) => !n.parentAssetId || !itemIds.has(n.parentAssetId))
    .sort(typeSort);

  return (
    <div className="asset-tree">
      <div className="asset-tree__header">
        {items.length} asset node{items.length !== 1 ? 's' : ''}
      </div>
      <ul className="asset-tree__list" role="tree">
        {roots.map((root) => (
          <AssetNodeRow
            key={root.id}
            node={root}
            getChildren={getChildren}
            depth={0}
          />
        ))}
      </ul>
    </div>
  );
}
